/**
 * QuickLinkGrid
 *
 * The quick-link tiles shared by WelcomeBanner and OnboardingThanks. Both used
 * to carry their own `QuickLink` interface, their own link array and their own
 * copy of the same `.map()` — same shape twice, different copy, which is the
 * templating fingerprint we don't want.
 *
 * The two callers stay deliberately distinct: they pass their own copy (the
 * banner orients, the thanks screen points at a next step) and their own
 * `layout`, so sharing the tile doesn't flatten them into the same block.
 *
 * Tiles are borderless on purpose. Both callers render inside a <Card>, and a
 * bordered tile inside a bordered card is card-in-card — one containment layer
 * is enough. Separation comes from the tinted rest state + hover instead.
 */

import { cn } from '@xhubio-saas/core-frontend/ui-components'
import type { LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

export interface QuickLinkItem {
  href: string
  icon: LucideIcon
  title: string
  description: string
}

interface QuickLinkGridProps {
  links: QuickLinkItem[]
  /**
   * `even` — one equal column per link (WelcomeBanner's four areas are peers).
   * `lead` — the first link spans the full row above the rest, for callers
   *   whose first link is the actual next step rather than one option of many.
   */
  layout?: 'even' | 'lead'
  className?: string
}

export function QuickLinkGrid({ links, layout = 'even', className }: QuickLinkGridProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-3',
        layout === 'even' ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2',
        className
      )}
    >
      {links.map((link, index) => {
        const Icon = link.icon
        const isLead = layout === 'lead' && index === 0
        return (
          <Link
            key={link.href}
            to={link.href}
            className={cn(
              'group flex items-start gap-3 rounded-md bg-background/60 p-3',
              'transition-colors hover:bg-accent',
              isLead && 'sm:col-span-2'
            )}
          >
            <div
              className={cn(
                'flex shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary',
                isLead ? 'h-10 w-10' : 'h-9 w-9'
              )}
            >
              <Icon className={isLead ? 'h-5 w-5' : 'h-[18px] w-[18px]'} />
            </div>
            <div className="min-w-0">
              <div
                className={cn(
                  'font-medium group-hover:text-primary',
                  isLead ? 'text-base' : 'text-sm'
                )}
              >
                {link.title}
              </div>
              <div className="text-xs text-muted-foreground line-clamp-2">{link.description}</div>
            </div>
          </Link>
        )
      })}
    </div>
  )
}
